import { useState, useCallback, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useOCRStore } from '@/stores/ocrStore';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { captureError } from '@/lib/sentry';
import { validateFile, calculateFileHash } from '@/lib/security/fileValidator';
import { invalidateFinancesChantier } from '@/lib/queryInvalidation';
import { z } from 'zod';
import type {
  OCRExtractedDocument,
  OCRExtractionResponse,
  MultiOCRFile,
  MultiOCRResultLine,
} from '@/types/ocr';

// === Schemas Zod (même pattern que useClaudeVisionOCR lignes 19-42) ===
const factureInsertSchema = z.object({
  fournisseur: z.string().max(200).default('Fournisseur inconnu'),
  montant_ht: z.number().nonnegative().max(100000000).nullable(),
  montant_ttc: z.number().nonnegative().max(100000000).nullable(),
  tva_pct: z.number().min(0).max(100).default(10),
  numero_facture: z.string().max(100).nullable().optional(),
  date_facture: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  siret: z.string().regex(/^\d{14}$/).nullable().optional().or(z.literal('')),
});

const devisInsertSchema = z.object({
  titre: z.string().max(200).default('Devis'),
  montant_ht: z.number().nonnegative().max(100000000).nullable(),
  montant_ttc: z.number().nonnegative().max(100000000).nullable(),
  tva: z.number().min(0).max(100).nullable(),
  statut: z.enum(['brouillon', 'envoye', 'accepte', 'refuse']).default('brouillon'),
});

const fraisInsertSchema = z.object({
  type_frais: z.string().min(1).max(100),
  montant_ht: z.number().nonnegative().max(100000000),
  description: z.string().max(500).optional().nullable(),
  date_frais: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

const MAX_FILES = 15;
const MAX_TOTAL_SIZE = 100 * 1024 * 1024; // 100 Mo
const DELAY_BETWEEN_FILES_MS = 1000;

/**
 * Hook multi-scan IA — extraction séquentielle + liaison aux chantiers
 * 100% inerte si addFiles() n'est pas appelé (aucun useEffect au mount sans garde)
 */
export function useMultiOCR(
  chantierId: string,
  documentType: 'facture' | 'devis' | 'frais',
  entrepriseId: string | null
) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const ocrStore = useOCRStore();
  const { canUseOCR, getRemainingOCR, incrementOCR } = useSubscription();

  const [files, setFiles] = useState<MultiOCRFile[]>([]);
  const [allResultLines, setAllResultLines] = useState<MultiOCRResultLine[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [restoredFromStore, setRestoredFromStore] = useState(false);

  const abortRef = useRef(false);
  const isMountedRef = useRef(true);
  // Ref pour que startProcessing puisse lire les fichiers immédiatement après addFiles
  const filesRef = useRef<MultiOCRFile[]>([]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // === Restauration depuis le store (Prompt I) ===
  // Gardé par chantierId truthy + session existante — ne fait RIEN sinon
  useEffect(() => {
    if (!chantierId || !documentType) return;
    const session = ocrStore.multiScanSession;
    if (!session) return;
    if (session.chantierId !== chantierId || session.documentType !== documentType) return;
    // Session valide trouvée — la restauration se fera via le bandeau dans ClaudeVisionImport
    // On ne restaure pas automatiquement ici pour éviter de passer en multiMode sans contrôle du parent
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // === Phase 1 : Ajout de fichiers ===
  const addFiles = useCallback(async (newFiles: File[]) => {
    // Validation par fichier
    const validFiles: { file: File; hash: string }[] = [];
    const seenHashes = new Set<string>();
    let totalSize = 0;

    for (const file of newFiles) {
      if (validFiles.length >= MAX_FILES) {
        toast({ title: 'Limite atteinte', description: `Maximum ${MAX_FILES} documents par lot.`, variant: 'destructive' });
        break;
      }

      const validation = validateFile(file, 'document');
      if (!validation.valid) {
        toast({ title: `${file.name}`, description: validation.error || 'Fichier invalide', variant: 'destructive' });
        continue;
      }

      totalSize += file.size;
      if (totalSize > MAX_TOTAL_SIZE) {
        toast({ title: 'Taille totale dépassée', description: 'Maximum 100 Mo par lot.', variant: 'destructive' });
        break;
      }

      // Dédoublon intra-batch
      const hash = await calculateFileHash(file);
      if (seenHashes.has(hash)) {
        toast({ title: `${file.name} ignoré`, description: 'Doublon dans le lot.' });
        continue;
      }
      seenHashes.add(hash);
      validFiles.push({ file, hash });
    }

    if (validFiles.length === 0) return;

    // Vérifier quota
    const remaining = getRemainingOCR();
    let filesToProcess = validFiles;
    if (remaining !== -1 && validFiles.length > remaining) {
      toast({
        title: 'Quota limité',
        description: `${remaining} scan${remaining > 1 ? 's' : ''} IA restant${remaining > 1 ? 's' : ''}. Seuls les ${remaining} premiers fichiers seront traités.`,
      });
      filesToProcess = validFiles.slice(0, remaining);
    }

    const multiFiles: MultiOCRFile[] = filesToProcess.map(({ file }) => ({
      id: crypto.randomUUID(),
      file,
      status: 'pending' as const,
      isMultiRef: false,
      resultLines: [],
    }));

    filesRef.current = multiFiles;
    setFiles(multiFiles);
    setAllResultLines([]);
    setCurrentIndex(0);
    abortRef.current = false;
  }, [toast, getRemainingOCR]);

  // === Phase 1 : Traitement séquentiel ===
  const startProcessing = useCallback(async () => {
    const currentFiles = filesRef.current;
    if (currentFiles.length === 0) return;
    setIsProcessing(true);
    abortRef.current = false;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !entrepriseId) {
      toast({ title: 'Non authentifié', description: 'Connectez-vous pour scanner.', variant: 'destructive' });
      setIsProcessing(false);
      return;
    }

    const accumulatedLines: MultiOCRResultLine[] = [];

    for (let i = 0; i < currentFiles.length; i++) {
      if (abortRef.current) break;
      if (!isMountedRef.current) break;

      // Vérifier quota avant chaque fichier
      if (!canUseOCR()) {
        toast({ title: 'Quota atteint', description: 'Quota de scans IA épuisé pour ce mois.' });
        break;
      }

      setCurrentIndex(i);
      const multiFile = currentFiles[i];

      // Marquer comme "extracting"
      setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'extracting' } : f));

      try {
        const result = await extractSingleFile(multiFile.file, entrepriseId);

        if (!isMountedRef.current) break;

        if (result) {
          // Éclater en lignes (multi-ref ou simple)
          const rawLines = buildResultLines(multiFile.id, multiFile.file.name, result);

          // Vérifier doublons en base (non bloquant)
          const lines = await checkDuplicatesForLines(rawLines);

          setFiles(prev => prev.map((f, idx) => idx === i ? {
            ...f,
            status: 'done',
            result: result.data,
            confidence: result.confidence,
            isMultiRef: lines.length > 1,
            resultLines: lines,
          } : f));

          accumulatedLines.push(...lines);
          setAllResultLines([...accumulatedLines]);

          // Incrémenter quota + sync store
          await incrementOCR();
          ocrStore.setMultiScanSession({
            chantierId,
            documentType,
            resultLines: [...accumulatedLines],
            createdAt: Date.now(),
          });
        } else {
          // Extraction échouée
          const errorLine: MultiOCRResultLine = {
            id: crypto.randomUUID(),
            fileName: multiFile.file.name,
            status: 'error',
            error: 'Document illisible ou non reconnu',
          };

          setFiles(prev => prev.map((f, idx) => idx === i ? {
            ...f,
            status: 'error',
            error: 'Extraction échouée',
            resultLines: [errorLine],
          } : f));

          accumulatedLines.push(errorLine);
          setAllResultLines([...accumulatedLines]);

          ocrStore.setMultiScanSession({
            chantierId,
            documentType,
            resultLines: [...accumulatedLines],
            createdAt: Date.now(),
          });
        }
      } catch (error: any) {
        console.error('[MultiOCR] Erreur extraction:', error);
        captureError(error, { context: 'multi_ocr_extract', fileName: multiFile.file.name });

        const errorLine: MultiOCRResultLine = {
          id: crypto.randomUUID(),
          fileName: multiFile.file.name,
          status: 'error',
          error: error.message || 'Erreur inconnue',
        };

        setFiles(prev => prev.map((f, idx) => idx === i ? {
          ...f,
          status: 'error',
          error: error.message,
          resultLines: [errorLine],
        } : f));

        accumulatedLines.push(errorLine);
        setAllResultLines([...accumulatedLines]);
      }

      // Délai entre fichiers (rate limit)
      if (i < currentFiles.length - 1 && !abortRef.current) {
        await new Promise(r => setTimeout(r, DELAY_BETWEEN_FILES_MS));
      }
    }

    // Supprimer les fichiers non traités (si annulé)
    if (abortRef.current) {
      setFiles(prev => prev.filter(f => f.status !== 'pending'));
    }

    setIsProcessing(false);
  }, [entrepriseId, chantierId, documentType, toast, canUseOCR, incrementOCR, ocrStore]);

  // === Extraction d'un fichier individuel ===
  const extractSingleFile = async (
    file: File,
    entId: string
  ): Promise<{ data: OCRExtractedDocument; confidence: number } | null> => {
    // Check cache
    const fileHash = await calculateFileHash(file);
    const { data: cached } = await supabase
      .from('ocr_cache')
      .select('ocr_result, confidence')
      .eq('entreprise_id', entId)
      .eq('file_hash', fileHash)
      .maybeSingle();

    if (cached) {
      await supabase.from('ocr_cache')
        .update({ last_used_at: new Date().toISOString() })
        .eq('entreprise_id', entId)
        .eq('file_hash', fileHash);

      const cachedResponse = cached.ocr_result as OCRExtractionResponse;
      if (cachedResponse.success && cachedResponse.data) {
        return { data: cachedResponse.data, confidence: cachedResponse.confidence || cached.confidence || 0 };
      }
    }

    // Upload temporaire
    const bucket = documentType === 'facture' ? 'factures' :
                   documentType === 'frais' ? 'factures' : 'devis';
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const tempPath = `${entId}/temp-${Date.now()}-${sanitizedFileName}`;

    const { error: uploadError } = await supabase.storage.from(bucket).upload(tempPath, file);
    if (uploadError) throw uploadError;

    try {
      const { data: urlData } = await supabase.storage.from(bucket).createSignedUrl(tempPath, 3600);
      if (!urlData?.signedUrl) throw new Error("Impossible de générer l'URL signée");

      const edgeFnDocType = documentType === 'frais' ? 'frais_annexe' : 'facture';
      const { data, error } = await supabase.functions.invoke('ocr-claude-vision', {
        body: { fileUrl: urlData.signedUrl, documentType: edgeFnDocType, entrepriseId: entId },
      });

      if (error) throw new Error(error.message || "Erreur d'extraction");

      const response = data as OCRExtractionResponse;

      // Stocker en cache
      try {
        await supabase.from('ocr_cache').upsert({
          entreprise_id: entId,
          file_hash: fileHash,
          file_name: file.name,
          file_size: file.size,
          document_type: documentType,
          ocr_result: response,
          confidence: response.success ? response.confidence : 0,
        }, { onConflict: 'entreprise_id,file_hash' });
      } catch {
        // Cache non bloquant
      }

      if (response.success && response.data) {
        return { data: response.data, confidence: response.confidence };
      }
      if ('partialData' in response && response.partialData) {
        return { data: response.partialData, confidence: response.confidence || 0 };
      }
      return null;
    } finally {
      // Cleanup temp
      await supabase.storage.from(bucket).remove([tempPath]);
    }
  };

  // === Construction des lignes résultat (éclatement multi-ref) ===
  const buildResultLines = (
    fileId: string,
    fileName: string,
    result: { data: OCRExtractedDocument; confidence: number }
  ): MultiOCRResultLine[] => {
    const { data, confidence } = result;

    // Test multi-ref : commandes.length > 1
    if (data.commandes && data.commandes.length > 1) {
      return data.commandes.map((cmd) => {
        // Filtrer les lignes par lignes_indices
        const filteredLignes = data.lignes.filter((_, idx) => cmd.lignes_indices.includes(idx));
        const tauxTVA = data.totaux?.taux_tva || 10;
        const montantTVA = cmd.total_ht * tauxTVA / 100;

        const subDocument: OCRExtractedDocument = {
          ...data,
          lignes: filteredLignes,
          totaux: {
            ...data.totaux,
            total_ht: cmd.total_ht,
            total_ttc: cmd.total_ttc,
            montant_tva: montantTVA,
          },
          commandes: [cmd],
        };

        return {
          id: crypto.randomUUID(),
          fileName,
          parentFileId: fileId,
          status: 'done' as const,
          result: subDocument,
          confidence,
          montantTTC: cmd.total_ttc,
          reference: cmd.reference || undefined,
        };
      });
    }

    // Fichier simple — 1 ligne
    return [{
      id: crypto.randomUUID(),
      fileName,
      status: 'done' as const,
      result: data,
      confidence,
      montantTTC: data.totaux?.total_ttc || undefined,
    }];
  };

  // === Vérification doublons en base ===
  const checkDuplicatesForLines = async (
    lines: MultiOCRResultLine[]
  ): Promise<MultiOCRResultLine[]> => {
    if (!entrepriseId || documentType === 'frais') return lines;

    const hashes = lines
      .filter(l => l.status === 'done' && l.result?.hash_anti_doublon)
      .map(l => l.result!.hash_anti_doublon!);

    if (hashes.length === 0) return lines;

    try {
      const table = documentType === 'facture' ? 'factures_fournisseurs' : 'devis';

      const { data: existing } = await supabase
        .from(table)
        .select('hash_anti_doublon, chantier_id')
        .eq('entreprise_id', entrepriseId)
        .in('hash_anti_doublon', hashes);

      if (!existing || existing.length === 0) return lines;

      // Récupérer les noms des chantiers liés
      const chantierIds = [...new Set(existing.map(r => r.chantier_id))];
      const { data: chantiers } = await supabase
        .from('chantiers')
        .select('id, nom_chantier')
        .in('id', chantierIds);

      const nameMap = new Map(chantiers?.map(c => [c.id, c.nom_chantier]) || []);
      const hashToChantier = new Map<string, string>();
      for (const row of existing) {
        if (row.hash_anti_doublon) {
          hashToChantier.set(row.hash_anti_doublon, nameMap.get(row.chantier_id) || 'un chantier');
        }
      }

      return lines.map(line => {
        if (line.status !== 'done' || !line.result?.hash_anti_doublon) return line;
        const match = hashToChantier.get(line.result.hash_anti_doublon);
        if (match) {
          return { ...line, duplicateWarning: `Déjà enregistré — chantier "${match}"` };
        }
        return line;
      });
    } catch (e) {
      console.error('[MultiOCR] Erreur vérification doublons:', e);
      return lines; // Non bloquant
    }
  };

  // === Phase 2 : Liaison aux chantiers ===

  const saveLine = async (
    line: MultiOCRResultLine,
    targetChantierId: string,
    targetChantierNom: string
  ): Promise<boolean> => {
    if (!line.result || !entrepriseId) return false;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const finalData = line.result;

    try {
      if (documentType === 'facture') {
        const ht = finalData.totaux?.total_ht || 0;
        const tvaPct = finalData.totaux?.taux_tva || 10;
        const ttc = ht > 0 ? ht * (1 + tvaPct / 100) : (finalData.totaux?.total_ttc || null);

        const validation = factureInsertSchema.safeParse({
          fournisseur: finalData.emetteur?.nom || 'Fournisseur inconnu',
          montant_ht: ht || null,
          montant_ttc: ttc,
          tva_pct: tvaPct,
          numero_facture: finalData.numero_document,
          date_facture: finalData.date_document,
          siret: finalData.emetteur?.siret || null,
        });
        if (!validation.success) throw new Error(`Données invalides: ${validation.error.issues.map(e => e.message).join(', ')}`);

        const { error } = await supabase.from('factures_fournisseurs').insert({
          entreprise_id: entrepriseId,
          created_by: user.id,
          chantier_id: targetChantierId,
          fournisseur: validation.data.fournisseur,
          categorie: 'Autres',
          numero_facture: validation.data.numero_facture,
          date_facture: validation.data.date_facture,
          montant_ht: validation.data.montant_ht,
          montant_ttc: validation.data.montant_ttc,
          tva_pct: validation.data.tva_pct,
          siret: validation.data.siret,
          extraction_json: finalData as any,
        });
        if (error) throw error;

      } else if (documentType === 'frais') {
        const ht = finalData.totaux?.total_ht || 0;
        const validation = fraisInsertSchema.safeParse({
          type_frais: 'Autre',
          montant_ht: ht,
          date_frais: finalData.date_document || new Date().toISOString().split('T')[0],
          description: finalData.objet_travaux || finalData.emetteur?.nom || 'Importé par Scan IA',
        });
        if (!validation.success) throw new Error(`Données invalides: ${validation.error.issues.map(e => e.message).join(', ')}`);

        const { error } = await supabase.from('frais_chantier').insert({
          entreprise_id: entrepriseId,
          chantier_id: targetChantierId,
          type_frais: validation.data.type_frais,
          montant_ht: validation.data.montant_ht,
          montant_total: validation.data.montant_ht,
          date_frais: validation.data.date_frais,
          description: validation.data.description,
        });
        if (error) throw error;

      } else {
        // Devis
        const validation = devisInsertSchema.safeParse({
          titre: finalData.objet_travaux || 'Devis',
          montant_ht: finalData.totaux?.total_ht ?? null,
          montant_ttc: finalData.totaux?.total_ttc ?? null,
          tva: finalData.totaux?.taux_tva ?? null,
          statut: 'brouillon',
        });
        if (!validation.success) throw new Error(`Données invalides: ${validation.error.issues.map(e => e.message).join(', ')}`);

        const { error } = await supabase.from('devis').insert({
          entreprise_id: entrepriseId,
          chantier_id: targetChantierId,
          titre: validation.data.titre,
          montant_ht: validation.data.montant_ht,
          montant_ttc: validation.data.montant_ttc,
          tva: validation.data.tva,
          extraction_json: finalData as any,
          statut: validation.data.statut,
        });
        if (error) throw error;
      }

      // Succès : marquer la ligne comme saved
      const updateLine = (lines: MultiOCRResultLine[]) =>
        lines.map(l => l.id === line.id ? {
          ...l,
          status: 'saved' as const,
          linkedChantierId: targetChantierId,
          linkedChantierNom: targetChantierNom,
        } : l);

      setAllResultLines(prev => updateLine(prev));
      setFiles(prev => prev.map(f => ({
        ...f,
        resultLines: updateLine(f.resultLines),
      })));

      // Sync store + invalidation react-query
      ocrStore.updateMultiScanLine(line.id, {
        status: 'saved',
        linkedChantierId: targetChantierId,
        linkedChantierNom: targetChantierNom,
      });
      invalidateFinancesChantier(queryClient, entrepriseId, targetChantierId);

      return true;
    } catch (error: any) {
      console.error('[MultiOCR] Erreur sauvegarde:', error);
      captureError(error, { context: 'multi_ocr_save', lineId: line.id, documentType });
      toast({ title: 'Erreur', description: error.message, variant: 'destructive' });
      return false;
    }
  };

  // === Actions de liaison ===

  const linkToCurrentChantier = useCallback(async (lineId: string) => {
    const line = allResultLines.find(l => l.id === lineId);
    if (!line) return;

    // Récupérer le nom du chantier actuel
    const { data: chantier } = await supabase
      .from('chantiers')
      .select('nom_chantier')
      .eq('id', chantierId)
      .single();

    await saveLine(line, chantierId, chantier?.nom_chantier || 'Ce chantier');
  }, [allResultLines, chantierId]);

  const linkToOtherChantier = useCallback(async (lineId: string, targetId: string, targetNom: string) => {
    const line = allResultLines.find(l => l.id === lineId);
    if (!line) return;
    await saveLine(line, targetId, targetNom);
  }, [allResultLines]);

  const createAndLink = useCallback(async (lineId: string, newChantierId: string) => {
    const line = allResultLines.find(l => l.id === lineId);
    if (!line) return;

    // Fetch le nom du chantier créé
    const { data: chantier } = await supabase
      .from('chantiers')
      .select('nom_chantier')
      .eq('id', newChantierId)
      .single();

    await saveLine(line, newChantierId, chantier?.nom_chantier || 'Nouveau chantier');
  }, [allResultLines]);

  const linkAllToCurrentChantier = useCallback(async (lineIds?: Set<string>) => {
    const { data: chantier } = await supabase
      .from('chantiers')
      .select('nom_chantier')
      .eq('id', chantierId)
      .single();

    const chantierNom = chantier?.nom_chantier || 'Ce chantier';
    const linesToLink = allResultLines.filter(l =>
      l.status === 'done' && (!lineIds || lineIds.has(l.id))
    );

    for (const line of linesToLink) {
      await saveLine(line, chantierId, chantierNom);
    }
  }, [allResultLines, chantierId]);

  const cancelProcessing = useCallback(() => {
    abortRef.current = true;
  }, []);

  const retryExtraction = useCallback(async (lineId: string) => {
    // Trouver le fichier source de cette ligne
    const parentFile = files.find(f => f.resultLines.some(l => l.id === lineId));
    if (!parentFile || !entrepriseId) return;

    // Re-extraire
    const updateFileStatus = (status: MultiOCRFile['status']) => {
      setFiles(prev => prev.map(f => f.id === parentFile.id ? { ...f, status } : f));
    };

    updateFileStatus('extracting');

    try {
      const result = await extractSingleFile(parentFile.file, entrepriseId);
      if (result) {
        const lines = buildResultLines(parentFile.id, parentFile.file.name, result);

        setFiles(prev => prev.map(f => f.id === parentFile.id ? {
          ...f, status: 'done', result: result.data, confidence: result.confidence,
          isMultiRef: lines.length > 1, resultLines: lines,
        } : f));

        // Remplacer la ligne erreur par les nouvelles lignes
        setAllResultLines(prev => {
          const filtered = prev.filter(l => l.id !== lineId);
          return [...filtered, ...lines];
        });

        await incrementOCR();

        // Sync store
        const updatedLines = allResultLines.filter(l => l.id !== lineId).concat(lines);
        ocrStore.setMultiScanSession({
          chantierId, documentType, resultLines: updatedLines, createdAt: Date.now(),
        });
      } else {
        updateFileStatus('error');
      }
    } catch (error: any) {
      console.error('[MultiOCR] Erreur retry:', error);
      updateFileStatus('error');
      toast({ title: 'Erreur', description: error.message, variant: 'destructive' });
    }
  }, [files, entrepriseId, chantierId, documentType, allResultLines, toast, incrementOCR, ocrStore]);

  const dismissLine = useCallback((lineId: string) => {
    setAllResultLines(prev => prev.filter(l => l.id !== lineId));
    setFiles(prev => prev.map(f => ({
      ...f,
      resultLines: f.resultLines.filter(l => l.id !== lineId),
    })));

    // Sync store
    const updatedLines = allResultLines.filter(l => l.id !== lineId);
    if (updatedLines.length > 0) {
      ocrStore.setMultiScanSession({
        chantierId, documentType, resultLines: updatedLines, createdAt: Date.now(),
      });
    } else {
      ocrStore.clearMultiScanSession();
    }
  }, [allResultLines, chantierId, documentType, ocrStore]);

  const removeFile = useCallback((fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  }, []);

  // === Restauration depuis le store (appelée par ClaudeVisionImport) ===
  const restoreFromStore = useCallback(() => {
    const session = ocrStore.multiScanSession;
    if (!session || session.chantierId !== chantierId || session.documentType !== documentType) return false;

    setAllResultLines(session.resultLines);
    setRestoredFromStore(true);
    toast({ title: 'Session restaurée', description: 'Résultats de scan précédents restaurés.' });
    return true;
  }, [chantierId, documentType, ocrStore, toast]);

  // === Nettoyage auto quand tout est sauvé ===
  useEffect(() => {
    if (allResultLines.length === 0) return;
    const allSavedOrError = allResultLines.every(l => l.status === 'saved' || l.status === 'error');
    const allSaved = allResultLines.every(l => l.status === 'saved');
    if (allSaved) {
      ocrStore.clearMultiScanSession();
    }
  }, [allResultLines]); // eslint-disable-line react-hooks/exhaustive-deps

  // === Compteurs ===
  const completedCount = files.filter(f => f.status === 'done' || f.status === 'error').length;
  const totalCount = files.length;
  const savedCount = allResultLines.filter(l => l.status === 'saved').length;
  const pendingSaveCount = allResultLines.filter(l => l.status === 'done').length;

  return {
    files,
    allResultLines,
    addFiles,
    removeFile,
    startProcessing,
    cancelProcessing,
    linkToCurrentChantier,
    linkToOtherChantier,
    createAndLink,
    linkAllToCurrentChantier,
    retryExtraction,
    dismissLine,
    restoreFromStore,
    isProcessing,
    currentIndex,
    completedCount,
    totalCount,
    savedCount,
    pendingSaveCount,
    restoredFromStore,
  };
}
